import { IconButton } from "@material-ui/core";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { THEME } from "../../theme/ThemeStore";
import { abbreviatedLanguageOptions, allStatusOptions } from "../../constants";
import { t } from "../../i18n/util";
import { API } from "../../network/API";
import { ApplicationStateEnum, AssetUploadResponse } from "../../network/APITypes";
import { generalStore } from "../../stores/GeneralStore";
import { EMPTY_DIALOG_FORM, hrStore } from "../../stores/HrStore";
import { IApplicationStateEnum, JobCategoryEnum } from "../../types/models";
import { pushRoute } from "../app/router/history";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { ConfirmationDialog } from "../ui/ConfirmationDialog";
import {
    ApplicantPicture,
    DivisionTag,
    HrBotBackButton,
    HrBotOrangeButton,
    HrBotOrangeMultipleButton,
    NoApplicantPicture
} from "../ui/Primitives";
import {
    optionToString,
    statusOptionToPossibleDestinations,
    templateToString,
    transitionToTemplate
} from "../util/helpers";
import { HrKanbanDialog } from "./dialogs/HrKanbanDialog";
import { HrRoutes } from "./router/HrRoutes";
import { authStore } from "../../stores/AuthStore";
import { BackButton } from "../ui/BackButton";

export const ApplicantDetailsHeader = observer(() => {
    const isMobile = useMobileMediaQuery();
    const isDal = THEME.THEME_NAME === "dal";

    // type for HrKanbanDialog
    const [transitionDestination, setTransitionDestination] = React.useState<IApplicationStateEnum | undefined>(
        undefined
    );
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

    const handleBack = () => {
        pushRoute(hrStore.backTarget);
    };

    //#region handle transitioning of applicant

    const handleSelectTransitionDestination = (status: IApplicationStateEnum) => {
        // if there is no HrKanbanDialog skip it and immmediately transition the applicant
        if (status === "application_received") {
            hrStore.currentDialogForm.noMessage = true;
            hrStore.currentDialogForm.message = "";
            handleSubmitTransitionDialog(status);
        } else {
            const template = transitionToTemplate(currentStatus(), status, hrStore.currentApplicant.jobCategory);

            let title = "";

            if (status === "first_interview") {
                title = t("screen.dashboard.dialog.firstInterview.title", {
                    name: `${hrStore.currentApplicant.firstName} ${hrStore.currentApplicant.lastName}`,
                    company: t(isDal ? "company.dal" : "company.aaa")
                });
            } else if (status === "second_interview") {
                title = t("screen.dashboard.dialog.secondInterview.title", {
                    name: `${hrStore.currentApplicant.firstName} ${hrStore.currentApplicant.lastName}`,
                    company: t(isDal ? "company.dal" : "company.aaa")
                });
            }

            hrStore.currentDialogForm = {
                ...EMPTY_DIALOG_FORM,
                template: template,
                message: templateToString(
                    hrStore.currentApplicant.language ?? abbreviatedLanguageOptions[0].value,
                    template,
                    { firstName: hrStore.currentApplicant.firstName, userName: authStore.givenName ?? "" }
                ),
                title: title,
                language: hrStore.currentApplicant.language ?? abbreviatedLanguageOptions[0].value,
                evidence: status === "evidence" ? "true" : "false"
            };

            setTransitionDestination(status);
        }
    };

    const handleSubmitTransitionDialog = async (type?: IApplicationStateEnum) => {
        try {
            generalStore.isLoading = true;
            if (hrStore.currentApplicantID && type) {
                if (type === "hired" && hrStore.currentDialogForm.contract) {
                    const res: AssetUploadResponse = await API.postAsset(
                        hrStore.currentDialogForm.contract,
                        generalStore.locale
                    );
                    await API.postTransition(hrStore.currentApplicantID, hrStore.currentDialogForm, type, res.id);
                    // when evidence is selected open the "denied" dialog
                } else if (type === "evidence") {
                    await API.postTransition(hrStore.currentApplicantID, hrStore.currentDialogForm, "denied");
                } else {
                    await API.postTransition(hrStore.currentApplicantID, hrStore.currentDialogForm, type);
                }
            }
        } catch (err) {
            generalStore.setError(t("error.transferApplicant"), err);
        } finally {
            setTransitionDestination(undefined);
            hrStore.currentDialogForm = EMPTY_DIALOG_FORM;
            hrStore.loadApplicant();
            generalStore.isLoading = false;
        }
    };

    const handleCloseTransitionDialog = () => {
        setTransitionDestination(undefined);
        hrStore.currentDialogForm = EMPTY_DIALOG_FORM;
    };

    //#endregion

    //#region handle applicant deletion

    const handleOpenDeleteDialog = () => {
        setOpenDeleteDialog(true);
    };

    const handleDelete = async () => {
        try {
            generalStore.isLoading = true;
            await API.deleteApplicant(hrStore.currentApplicantID);
            pushRoute(HrRoutes.DASHBOARD);
        } catch (err) {
            generalStore.setError(t("error.deleteApplication"), err);
        } finally {
            generalStore.isLoading = false;
        }
    };

    const handleCloseDeleteApplicantDialog = () => {
        setOpenDeleteDialog(false);
    };

    //#endregion

    const currentStatus: () => IApplicationStateEnum | undefined = () => {
        let status: ApplicationStateEnum = "application_received";
        if (hrStore.currentApplicant.history) {
            status = hrStore.currentApplicant.history[hrStore.currentApplicant.history?.length - 1]?.toApplicationState;
        }

        if (status && hrStore.currentApplicant.evidence && status === "denied") {
            return "evidence";
        } else {
            return status;
        }
    };

    return (
        <>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <BackButton onClick={handleBack} style={{ margin: "16px auto 30px 0" }} />
                <div
                    style={{
                        flex: 1,
                        justifyContent: "space-between",
                        alignItems: "center",
                        display: "flex",
                        marginBottom: 20,
                        gap: 16,
                        flexDirection: isMobile ? "column" : "row"
                    }}
                >
                    <ApplicantDetailsHeaderProfile />
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: isMobile ? "row-reverse" : "row",
                            gap: isMobile ? 0 : 16,
                            flexGrow: 1,
                            justifyContent: "flex-end"
                        }}
                    >
                        <IconButton onClick={handleOpenDeleteDialog}>
                            <img src={THEME.Images.icDelete} alt="" />
                        </IconButton>
                        {currentStatus() !== "hired" ? (
                            <HrBotOrangeMultipleButton
                                label={optionToString(allStatusOptions, currentStatus())}
                                options={statusOptionToPossibleDestinations(currentStatus() as IApplicationStateEnum)}
                                onSelect={handleSelectTransitionDestination}
                            />
                        ) : (
                            <HrBotOrangeButton
                                label={optionToString(allStatusOptions, currentStatus())}
                                style={{ cursor: "default", justifyContent: "flex-start" }}
                            />
                        )}
                    </div>
                </div>
            </div>
            <HrKanbanDialog
                onSubmit={handleSubmitTransitionDialog}
                onBack={handleCloseTransitionDialog}
                fromApplicationState={currentStatus()}
                type={transitionDestination}
                firstName={hrStore.currentApplicant.firstName}
                jobCategory={hrStore.currentApplicant.jobCategory}
            />

            <ConfirmationDialog
                isOpen={openDeleteDialog}
                onBack={handleCloseDeleteApplicantDialog}
                onSubmit={handleDelete}
                label={
                    <FormattedMessage
                        id="screen.applicantDetails.deleteConfirmationText"
                        values={{
                            applicant: `${hrStore.currentApplicant.firstName} ${hrStore.currentApplicant.lastName}`,
                            b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                        }}
                    />
                }
            />
        </>
    );
});

const ApplicantDetailsHeaderProfile = observer(() => {
    const isMobile = useMobileMediaQuery();
    return (
        <div style={{ display: "flex", flex: 3, width: isMobile ? "100%" : "unset" }}>
            {hrStore.currentApplicant.photo ? (
                <ApplicantPicture
                    style={{
                        backgroundImage: `url(${hrStore.currentApplicant.photo.url})`,
                        height: 64,
                        minWidth: 64,
                        width: 64,
                        marginRight: 16
                    }}
                />
            ) : (
                <NoApplicantPicture
                    style={{
                        height: 64,
                        minWidth: 64,
                        width: 64,
                        marginRight: 16,
                        fontSize: 18
                    }}
                >{`${hrStore.currentApplicant.firstName[0] ?? ""}${hrStore.currentApplicant.lastName[0] ?? ""}${
                    hrStore.currentApplicant.lastName[1] ?? ""
                }`}</NoApplicantPicture>
            )}

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    flexGrow: 1,
                    width: 0
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 16 }} className="h3">
                    <div
                        style={{
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis"
                        }}
                    >
                        {`${hrStore.currentApplicant.firstName} ${hrStore.currentApplicant.lastName}`}
                    </div>
                    <DivisionTag division={hrStore.currentApplicant.jobCategory as JobCategoryEnum} />
                </div>
                <p className="body2" style={{ fontSize: 12 }}>
                    {t("screen.dashboard.applicationSubmitted")}{" "}
                    {moment(hrStore.currentApplicant.applicationDate).format(t("screen.dateFormat"))}
                </p>
            </div>
        </div>
    );
});
